import React, { FC, useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import MuiContainer from '@mui/material/Container';
import MuiGrid from '@mui/material/Grid';
import Button from 'src/components/Button';
import Typography from 'src/components/Typography';
import TextField from 'src/components/TextField';
import translate from '@translate';
import style from './style';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { checkFormValidity } from '../../utils/formFunctions';
import Loading from 'src/components/Loading/intex';
import appLogo from 'src/utils/appLogo';
import LangSelect from 'src/components/LangSelect';
import Fade from '@mui/material/Fade';
import { RootStoreType } from 'src/types/store';
import { EyeIcon } from 'src/components/SkladonIcon/SmallIcons';
import { Logo, LogoBlock } from '../LoginContainer';
import { resetPasswordFetch, clearResetPassword } from 'src/resources/resetPassword/reducer';
import StyledLink from 'src/components/StyledLink';
import { styled } from '@mui/system';
import { colors } from 'src/themes/default';

const TIMEOUT_ERROR_MESSAGE = 5000;
const initData = { NEW_PASSWORD: '', CHECK_PASSWORD: '' };
const initErrors: { [key: string]: boolean } = {
    NEW_PASSWORD: false,
    CHECK_PASSWORD: false,
};

export const MessageBlock = styled('div')<{
    type: 'success' | 'error';
}>(({ theme, type }) => ({
    marginTop: '10px',
    width: '100%',
    textAlign: 'center',
    padding: '10px 0px',
    backgroundColor: type === 'error' ? colors.alert_D2 : colors.accent_D2,
}));

const checkPassowrd = (value: string) => {
    const strongRegex = new RegExp('^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,})');
    return strongRegex.test(value);
};

const ResetPasswordContainer: FC = () => {
    const dispatch = useDispatch();
    const loading = useSelector((state: RootStoreType) => state.resetPassword.loading);
    const error = useSelector((state: RootStoreType) => state.resetPassword.error);
    const success = useSelector((state: RootStoreType) => state.resetPassword.success);

    const hash = location.pathname.split('/')[3];

    const formRef = useRef<HTMLFormElement>(null);
    const [editData, setEditData] = useState(initData);
    const [editErrors, setEditErrors] = useState(initErrors);
    const [visibleNewPassword, setVisibleNewPassword] = useState(false);
    const [visibleCheckPassword, setVisibleCheckPassword] = useState(false);
    const [apiError, setApiError] = useState(false);
    const [apiErrorTimeout, setApiErrorTimeout] = useState(-1);

    useEffect(() => {
        return () => {
            dispatch(clearResetPassword());
        };
    }, []);

    useEffect(() => {
        if (error) {
            setApiError(true);
            setApiErrorTimeout(
                window.setTimeout((): void => {
                    setApiError(false);
                }, TIMEOUT_ERROR_MESSAGE)
            );
        } else {
            window.clearTimeout(apiErrorTimeout);
            setApiError(false);
        }
    }, [error]);

    const errorCompare = editErrors.CHECK_PASSWORD
        ? translate('resetPassword.incorretPasswordText')
        : editData.CHECK_PASSWORD !== editData.NEW_PASSWORD
        ? translate('resetPassword.incorretPasswordMatchText')
        : '';

    const handleSubmit = (e: React.SyntheticEvent) => {
        e.stopPropagation();
        e.preventDefault();
        let newFormErrors = checkFormValidity(formRef);

        if (!Object.keys(newFormErrors).length) {
            if (!checkPassowrd(editData.NEW_PASSWORD)) {
                newFormErrors.NEW_PASSWORD = true;
            } else if (errorCompare) {
                newFormErrors.CHECK_PASSWORD = true;
            }
            dispatch(resetPasswordFetch({ hash, password: editData.NEW_PASSWORD }));
        }

        setEditErrors(newFormErrors);
    };

    const onChange = (key: 'NEW_PASSWORD' | 'CHECK_PASSWORD', value: any, error: boolean) => {
        const newData = { ...editData };
        const newErrors = { ...editErrors };
        newData[key] = value;

        if (error || !checkPassowrd(value)) {
            newErrors[key] = true;
        } else {
            delete newErrors[key];
        }

        setEditData(newData);
        setEditErrors(newErrors);
    };

    return (
        <MuiContainer fixed maxWidth="xs" disableGutters sx={{ display: 'flex', height: '100%' }}>
            <div className="Login" style={style.app}>
                {loading && <Loading />}
                <form ref={formRef} onSubmit={handleSubmit}>
                    <MuiGrid container columns={1}>
                        <MuiGrid item xs={1}>
                            <LogoBlock>
                                <Logo src={appLogo} alt="skladon background" />
                            </LogoBlock>
                        </MuiGrid>
                        <MuiGrid item xs={1}>
                            <Typography variant="h2" gutterBottom>
                                {translate('resetPassword.title')}
                            </Typography>
                        </MuiGrid>
                        <MuiGrid item xs={1}>
                            <TextField
                                value={editData.NEW_PASSWORD}
                                error={editErrors.NEW_PASSWORD}
                                onChange={(value, error) => onChange('NEW_PASSWORD', value, error)}
                                label={translate('resetPassword.newPassword')}
                                name="NEW_PASSWORD"
                                type={!visibleNewPassword ? 'password' : 'text'}
                                required
                                fullWidth
                                endIcon={{
                                    onClick: () => {
                                        setVisibleNewPassword(!visibleNewPassword);
                                    },
                                    icon: visibleNewPassword ? <VisibilityOff /> : <EyeIcon />,
                                }}
                                errorHelperText={
                                    editData.NEW_PASSWORD &&
                                    editErrors.NEW_PASSWORD &&
                                    translate('resetPassword.incorretPasswordText')
                                }
                            />
                        </MuiGrid>
                        <MuiGrid item xs={1}>
                            <TextField
                                value={editData.CHECK_PASSWORD}
                                error={editErrors.CHECK_PASSWORD || !!errorCompare}
                                onChange={(value, error) => onChange('CHECK_PASSWORD', value, error)}
                                label={translate('resetPassword.comparePassword')}
                                name="CHECK_PASSWORD"
                                type={!visibleCheckPassword ? 'password' : 'text'}
                                required
                                fullWidth
                                endIcon={{
                                    onClick: () => {
                                        setVisibleCheckPassword(!visibleCheckPassword);
                                    },
                                    icon: visibleCheckPassword ? <VisibilityOff /> : <EyeIcon />,
                                }}
                                errorHelperText={editData.CHECK_PASSWORD && errorCompare}
                            />
                        </MuiGrid>
                        <MuiGrid item xs={1}>
                            <LangSelect />
                        </MuiGrid>
                        <MuiGrid item xs={1}>
                            {/* <Button type="button" fullWidth onClick={handleSubmit}> */}
                            <Button type="submit" fullWidth>
                                {translate('resetPassword.sumbit')}
                            </Button>
                        </MuiGrid>
                        <MuiGrid item xs={1}>
                            {error && (
                                <Fade in={apiError} timeout={{ enter: 1000, exit: 1000 }}>
                                    <MuiContainer>
                                        <MessageBlock type="error">
                                            <Typography align="center" display="inline">
                                                {translate('resetPassword.wrongData')}
                                            </Typography>
                                        </MessageBlock>
                                    </MuiContainer>
                                </Fade>
                            )}
                            {success && (
                                <>
                                    <MessageBlock type="success">
                                        <Typography align="center" display="inline">
                                            {translate('resetPassword.success')}
                                        </Typography>
                                    </MessageBlock>
                                    <StyledLink to="/">
                                        <Button
                                            variant="text"
                                            fullWidth
                                            sx={{ textAlign: 'left', paddingLeft: '0px' }}
                                            noBackground
                                        >
                                            <Typography variant="body2" gutterBottom>
                                                {translate('resetPassword.login')}
                                            </Typography>
                                        </Button>
                                    </StyledLink>
                                </>
                            )}
                        </MuiGrid>
                    </MuiGrid>
                </form>
            </div>
        </MuiContainer>
    );
};

export default ResetPasswordContainer;
